import PageSEO from '../../components/feature/PageSEO';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://arvakon.com';

const projectsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Industry Applications – Arvakon',
  url: `${siteUrl}/projects`,
  description: 'Arvakon provides independent technical verification across renewable energy, pressure equipment, structural steel, EPC multi-supplier, BESS, and heavy industrial manufacturing supply chains.',
  mainEntity: {
    '@type': 'ItemList',
    name: 'Industry Applications',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Renewable Energy Infrastructure' },
      { '@type': 'ListItem', position: 2, name: 'Pressure Equipment Manufacturing' },
      { '@type': 'ListItem', position: 3, name: 'Structural Steel Fabrication' },
      { '@type': 'ListItem', position: 4, name: 'EPC Multi-Supplier Projects' },
      { '@type': 'ListItem', position: 5, name: 'Energy Storage Systems (BESS)' },
      { '@type': 'ListItem', position: 6, name: 'Heavy Industrial Equipment' },
    ],
  },
};

export default function ProjectsPage() {
  const industries = [
    {
      title: 'Renewable Energy Infrastructure',
      typicalEquipment: [
        'Wind turbine tower sections and transition pieces',
        'Offshore substation structures',
        'Foundation components and monopiles',
        'Battery energy storage system (BESS) enclosures',
      ],
      typicalRisks: [
        'Multi-tier supply chain complexity',
        'Welding procedure qualification gaps',
        'Material substitution without technical review',
        'Coating system specification deviations',
      ],
      verificationFocus: [
        'Structural welding procedure review and witness',
        'Material traceability verification across sub-suppliers',
        'Protective coating application surveillance',
        'Dimensional and geometric tolerance verification',
      ],
    },
    {
      title: 'Pressure Equipment Manufacturing',
      typicalEquipment: [
        'ASME pressure vessels and heat exchangers',
        'Storage tanks and process equipment',
        'Piping systems and manifolds',
        'Pressure-retaining components',
      ],
      typicalRisks: [
        'Non-conforming material certifications',
        'Inadequate welding procedure qualification',
        'Incomplete non-destructive testing coverage',
        'Undocumented design or material changes',
      ],
      verificationFocus: [
        'Material certification review and traceability',
        'Welding procedure specification (WPS) and procedure qualification record (PQR) review',
        'NDT witness and documentation review',
        'Hydrostatic test witness and final inspection',
      ],
    },
    {
      title: 'Structural Steel Fabrication',
      typicalEquipment: [
        'Industrial building structural frames',
        'Offshore platform modules',
        'Bridge components and heavy structural assemblies',
        'Support structures for industrial equipment',
      ],
      typicalRisks: [
        'Inadequate welder qualification documentation',
        'Non-conforming welding consumables',
        'Insufficient weld inspection coverage',
        'Deviation from approved fabrication drawings',
      ],
      verificationFocus: [
        'Welder qualification verification',
        'Welding consumable control and traceability',
        'Visual and NDT inspection witness',
        'Dimensional inspection and fit-up verification',
      ],
    },
    {
      title: 'EPC Multi-Supplier Projects',
      typicalEquipment: [
        'Integrated process modules',
        'Multi-vendor equipment packages',
        'Engineered-to-order industrial systems',
        'Complex assemblies with multiple fabrication sources',
      ],
      typicalRisks: [
        'Inconsistent quality standards across suppliers',
        'Interface coordination failures',
        'Documentation gaps between supply chain tiers',
        'Delayed identification of non-conformances',
      ],
      verificationFocus: [
        'Supplier qualification and capability assessment',
        'Sub-tier surveillance and documentation review',
        'Interface verification and dimensional control',
        'Consolidated reporting across multiple fabrication sites',
      ],
    },
    {
      title: 'Energy Storage Systems (BESS)',
      typicalEquipment: [
        'Battery enclosure structures',
        'Fire suppression system integration',
        'HVAC and thermal management components',
        'Electrical and structural support systems',
      ],
      typicalRisks: [
        'Rapid design iteration without documentation control',
        'Non-standard fabrication practices',
        'Inadequate coating system specification',
        'Insufficient quality control at sub-tier suppliers',
      ],
      verificationFocus: [
        'Fabrication process surveillance',
        'Welding and coating inspection',
        'Material verification and traceability',
        'Sub-supplier oversight and documentation review',
      ],
    },
    {
      title: 'Heavy Industrial Equipment',
      typicalEquipment: [
        'Mining and material handling equipment',
        'Industrial machinery frames and housings',
        'Heavy-duty transport and lifting equipment',
        'Custom-engineered industrial components',
      ],
      typicalRisks: [
        'Non-standard material specifications',
        'Inadequate welding procedure development',
        'Limited in-house quality control resources',
        'Insufficient documentation of manufacturing processes',
      ],
      verificationFocus: [
        'Material specification review and verification',
        'Welding procedure qualification support',
        'In-process inspection and testing witness',
        'Final inspection and documentation review',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageSEO
        title="Industry Applications – Arvakon | Renewable Energy, Pressure Equipment & More"
        description="Arvakon provides independent technical verification across renewable energy infrastructure, pressure equipment manufacturing, structural steel fabrication, EPC multi-supplier projects, BESS, and heavy industrial equipment."
        keywords="industry applications, renewable energy verification, pressure equipment surveillance, structural steel inspection, EPC supplier oversight, BESS fabrication"
        canonical={`${siteUrl}/projects`}
        jsonLd={projectsJsonLd}
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <a href="/" className="text-dark-primary text-xl font-semibold tracking-tight">
            ARVAKON
          </a>
          <nav className="flex items-center gap-8">
            <a href="/" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Home
            </a>
            <a href="/services" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Services
            </a>
            <a href="/standards" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Standards
            </a>
            <a href="/projects" className="text-sm text-dark-primary font-medium">
              Industry Applications
            </a>
            <a href="/about" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              About
            </a>
            <a href="/contact" className="text-sm text-slate-600 hover:text-dark-primary transition-colors">
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-40 pb-24 px-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-20">
            <h1 className="font-serif text-4xl text-dark-primary mb-6 tracking-tight">
              Industry Applications
            </h1>
            <p className="text-slate-600 leading-7 max-w-4xl">
              Arvakon provides independent technical verification across multi-tier industrial supply chains. Services are structured to provide visibility into manufacturing processes for equipment purchasers and project stakeholders.
            </p>
          </div>

          {/* Industry Blocks */}
          <div className="space-y-12">
            {industries.map((industry, index) => (
              <div
                key={industry.title}
                className={`${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                } border border-slate-200 rounded-lg p-10`}
              >
                {/* Industry Title */}
                <h2 className="font-serif text-2xl text-dark-primary mb-8 tracking-tight">
                  {industry.title}
                </h2>

                {/* Three-Column Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Typical Equipment */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
                      Typical Equipment
                    </h3>
                    <ul className="space-y-3">
                      {industry.typicalEquipment.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-slate-600 leading-6">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Typical Risks */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
                      Typical Risks
                    </h3>
                    <ul className="space-y-3">
                      {industry.typicalRisks.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-slate-600 leading-6">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Verification Focus Areas */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
                      Verification Focus Areas
                    </h3>
                    <ul className="space-y-3">
                      {industry.verificationFocus.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-slate-600 leading-6">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Closing Statement */}
          <div className="mt-20 pt-12 border-t border-slate-200">
            <div className="bg-dark-primary p-10 rounded-lg">
              <p className="text-industrial-light text-base leading-7 max-w-4xl">
                Arvakon operates as an independent technical representative on behalf of equipment purchasers requiring structured visibility within complex manufacturing environments.
              </p>
            </div>
          </div>

          {/* Reporting & Deliverables */}
          <div className="mt-12 pt-10 border-t border-slate-200">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
              Reporting &amp; Deliverables
            </h4>
            <p className="text-sm text-slate-500 leading-6 mb-3">
              Verification activities within industry applications result in structured technical reporting aligned with project inspection and test plans.
            </p>
            <p className="text-sm text-slate-500 leading-6 mb-3">
              Typical deliverables may include:
            </p>
            <ul className="space-y-2 mb-3">
              {[
                'Site visit verification report',
                'Hold point release confirmation (where applicable)',
                'Welding and NDT review summary',
                'Material traceability validation record',
                'Coating inspection documentation',
                'Consolidated verification summary for procurement review',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-industrial-steel mt-2 flex-shrink-0"></div>
                  <span className="text-sm text-slate-500 leading-6">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-500 leading-6 mb-2">
              Deliverables are structured for integration into client documentation systems and regulatory submission packages where required.
            </p>
            <p className="text-sm text-slate-500 leading-6">
              For structured documentation requests, please submit a technical inquiry through the{' '}
              <a href="/contact" className="text-slate-600 underline underline-offset-2 hover:text-dark-primary transition-colors">
                Contact page
              </a>.
            </p>
            <p className="text-xs text-slate-400 leading-6 mt-3">
              Reporting structure version: RDS-01 | Aligned with ARV-VQD-001 Rev.1
            </p>
          </div>

          {/* Governance & Independence */}
          <div className="mt-12 pt-10 border-t border-slate-200">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
              Governance &amp; Independence
            </h4>
            <p className="text-sm text-slate-500 leading-6 mb-3">
              Verification activities within industry applications are performed under Arvakon's structured governance framework ensuring impartiality and defined authority boundaries.
            </p>
            <p className="text-sm text-slate-500 leading-6">
              For full governance details, please refer to the{' '}
              <a href="/about" className="text-slate-600 underline underline-offset-2 hover:text-dark-primary transition-colors">
                About Arvakon
              </a>{' '}
              page.
            </p>
          </div>

          {/* Procurement Documentation */}
          <div className="mt-12 pt-10 border-t border-slate-200">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">
              Procurement Documentation
            </h4>
            <p className="text-sm text-slate-500 leading-6 mb-3">
              Structured documentation summaries related to industry application scope may be provided upon formal request for procurement and vendor qualification processes.
            </p>
            <p className="text-sm text-slate-500 leading-6 mb-3">
              For documentation requests, please use the{' '}
              <a href="/contact" className="text-slate-600 underline underline-offset-2 hover:text-dark-primary transition-colors">
                Technical Inquiry
              </a>{' '}
              form.
            </p>
            <p className="text-xs text-slate-400 leading-6">
              Documentation reference: ARV-VQD-001 Rev.1 | Issue Date: 2025-03-02
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}